import React from "react";
import PropTypes from "prop-types";
import styled, { withTheme } from "styled-components";
import Card from "components/CardGrid/Card";
import { DateTime } from "luxon";
import { parseDateToMsecs } from "lib/date-helpers";
import useCurrentLocation, {
  useFallbackLocation,
} from "hooks/use-current-location";

const CardGridSection = styled.section`
  background-color: ${(props) => props.backgroundColor};
  padding: 3.25rem 1.375rem 1rem;
  text-align: center;

  ${({ theme }) => theme.mq.md`
    padding: 3.5rem 5rem 2.25rem;
  `}

  ${({ theme }) => theme.mq.lg`
    padding: 4.375rem 1.5rem 2.75rem;
  `}
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  font-size: 2.5rem;
  line-height: 1.125;
  letter-spacing: -0.0125rem;
  margin: 0 auto 1.75rem auto;
  text-align: center;

  ${({ theme }) => theme.mq.md`
    font-size: 2.875rem;
    margin: 0 0 2.25rem;

    br {
      display: none;
    }
  `}

  ${({ theme }) => theme.mq.lg`
    font-size: 3rem;
    margin-bottom: 3.5rem;
  `}
`;

const CardGridWrapper = styled.div`
  margin: 0 auto;
  max-width: 20rem;

  ${({ theme }) => theme.mq.md`
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: repeat(${(props) => props.columnsPerRow}, 1fr);
    max-width: 65.5rem;
  `}

  ${({ theme }) => theme.mq.lg`
    max-width: 65.5rem;
  `}
`;

const data = [
  {
    alt: "Vet examining dog's ear",
    id: "1",
    image: "/veterinary_landing.png",
    name: "Vets",
    s: "veterinary",
  },
  {
    alt: "Groomer bathing, trimming, and brushing a dog",
    id: "2",
    image: "/dog_grooming_landing.png",
    name: "Groomers",
    s: "dog_grooming",
  },
  {
    alt: "A dog misbehaving",
    id: "3",
    image: "/dog_training_landing.png",
    name: "Trainers",
    s: "dog_training",
  },
];

function CardGrid({ title, columnsPerRow, backgroundColor }) {
  // eslint-disable-next-line camelcase
  const { latitude, longitude, formatted_address } = useCurrentLocation() || {};
  const fbLocation = useFallbackLocation();
  const tomorrow = DateTime.now().plus({ days: 1 }).toJSDate();
  const tomorrowMsecs = parseDateToMsecs(tomorrow);

  const cards = data.map((item) => (
    <Card
      alt={item.alt}
      formattedAddress={formatted_address || fbLocation.formatted_address}
      hoverColor={item.hoverColor}
      image={item.image}
      key={item.id}
      name={item.name}
      s={item.s}
      latitude={latitude || fbLocation.latitude}
      longitude={longitude || fbLocation.longitude}
      // eslint-disable-next-line camelcase
      startDateTime={tomorrowMsecs}
    />
  ));

  return (
    <CardGridSection backgroundColor={backgroundColor}>
      <Title>{title}</Title>
      <CardGridWrapper columnsPerRow={columnsPerRow}>{cards}</CardGridWrapper>
    </CardGridSection>
  );
}

export default withTheme(CardGrid);

CardGrid.propTypes = {
  backgroundColor: PropTypes.string,
  columnsPerRow: PropTypes.number,
  title: PropTypes.shape({}),
};

CardGrid.defaultProps = {
  backgroundColor: "#FFECD9",
  columnsPerRow: 3,
  title: "Find your pet care crew",
};
